import { Loader2 } from "lucide-react";
import { EventCard } from "@/components/EventCard";
import { DraftCard } from "@/components/DraftCard";
import { ToolActivity } from "@/components/ToolActivity";
import type { Turn, AssistantTurn } from "@/lib/chatTypes";
import { cn } from "@/lib/utils";

function Markdownish({ text }: { text: string }) {
  // Lightweight: render plain text, preserving newlines and `inline code`.
  // We deliberately avoid dangerouslySetInnerHTML for security.
  const parts = text.split(/(`[^`]+`)/g);
  return (
    <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed">
      {parts.map((part, i) =>
        part.startsWith("`") && part.endsWith("`") ? (
          <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-sm">
            {part.slice(1, -1)}
          </code>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </p>
  );
}

function AssistantMessage({ turn }: { turn: AssistantTurn }) {
  const hasContent = turn.blocks.some(
    (b) => (b.type === "text" && b.text.trim()) || b.type !== "text"
  );
  return (
    <div className="space-y-3">
      {turn.blocks.map((block, i) => {
        if (block.type === "text") {
          if (!block.text.trim()) return null;
          return <Markdownish key={i} text={block.text} />;
        }
        if (block.type === "tool") {
          return <ToolActivity key={i} name={block.name} args={block.args} />;
        }
        if (block.type === "events") {
          return (
            <div key={i} className="grid gap-3">
              {block.events.map((ev) => (
                <EventCard key={ev.id} event={ev} />
              ))}
            </div>
          );
        }
        if (block.type === "draft") {
          return <DraftCard key={i} draft={block.draft} />;
        }
        return null;
      })}
      {turn.pending && (!hasContent || turn.blocks[turn.blocks.length - 1]?.type !== "text") && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Thinking…
        </div>
      )}
      {turn.error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {turn.error}
        </div>
      )}
    </div>
  );
}

export function MessageList({ turns }: { turns: Turn[] }) {
  return (
    <div className="space-y-6">
      {turns.map((turn) =>
        turn.role === "user" ? (
          <div key={turn.id} className="flex justify-end">
            <div
              className={cn(
                "max-w-[85%] rounded-2xl rounded-br-md bg-primary px-4 py-2.5",
                "text-[15px] leading-relaxed text-primary-foreground"
              )}
            >
              <p className="whitespace-pre-wrap break-words">{turn.text}</p>
            </div>
          </div>
        ) : (
          <div key={turn.id} className="flex">
            <div className="w-full max-w-[95%]">
              <AssistantMessage turn={turn} />
            </div>
          </div>
        )
      )}
    </div>
  );
}
